import { PlusOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import { useEffect, useState } from 'react';

import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { AccrualsService } from 'services/AccrualsService';

import { AccessControl } from 'components/AccessControl';
import ClientsSelect from 'components/ClientsSelect';
import { REQUEST_LOADING } from 'constants/alerts';
import dayjs from 'dayjs';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useQueryParam } from 'hooks/useQueryParam';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { AccrualsTable } from './AccrualsTable';

const PAGE_TITLE = 'Начисления';

function Accruals() {
  useHeaderChanger(PAGE_TITLE);
  useDocumentTitle(PAGE_TITLE);
  const clientId = useQueryParam('client_id');
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [rowItems, setRowItems] = useState([]);

  const [accruals, setAccruals] = useState({
    client: [],
    worker: [],
    clientTotals: {
      salary: null,
      accruals: null,
      opv: null,
      so: null,
      vosms: null,
    },
    workerTotals: {
      salary: null,
      accruals: null,
      opv: null,
      so: null,
      vosms: null,
      ipn: null,
      osms: null,
    },
    organization: null,
  });
  const { filters, handleFilters, resetFilters } = useFilters({
    name: undefined,
    period: undefined,
    f_salary: undefined,
    t_salary: undefined,
    f_accrual: undefined,
    t_accrual: undefined,
    f_opv: undefined,
    t_opv: undefined,
    f_so: undefined,
    t_so: undefined,
    f_vosms: undefined,
    t_vosms: undefined,
    f_osms: undefined,
    t_osms: undefined,
    f_ipn: undefined,
    t_ipn: undefined,
    client_id: null,
  });

  useEffect(() => {
    if (clientId) {
      setSelectedClient(Number(clientId));
    }
  }, []);

  useEffect(() => {
    fetchAccruals();
  }, [selectedClient, filters]);

  const fetchAccruals = async () => {
    if (!selectedClient) {
      return;
    }

    setLoading(true);

    try {
      const {
        taxes,
        total_accrual,
        total_ipn,
        total_opv,
        total_opvr,
        total_osms,
        total_salary,
        total_so,
        total_vsms,
      } = await AccrualsService.getClientAccruals(selectedClient, filters);

      setRowItems(taxes);
      setAccruals({
        clientTotals: {
          salary: total_salary,
          accruals: total_accrual,
          opv: total_opv,
          opvr: total_opvr,
          so: total_so,
          vosms: total_vsms,
          osms: total_osms,
          ipn: total_ipn,
        },
      });
    } catch (err) {
      message.error('Не удалось загрузить начисления');
    } finally {
      setLoading(false);
    }
  };

  const calculate = async () => {
    const hide = message.loading(REQUEST_LOADING);
    const currentDate = dayjs();

    const payload = {
      client_id: selectedClient,
      begin_date: currentDate
        .subtract(12, 'month')
        .startOf('month')
        .format('YYYY-MM-DD'),
      end_date: currentDate.format('YYYY-MM-DD'),
    };

    try {
      const isCalculated = await AccrualsService.recalculateAccounting(payload);

      if (isCalculated) {
        fetchAccruals();
        message.success('Отчетность успешно пересчитано');
      }
    } catch (err) {
      message.error('Не удалось пересчитать отчетность');
    } finally {
      hide();
    }
  };

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
    setSelectedClient(id);
  };

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          pageTitle={PAGE_TITLE}
          clientId={clientId}
          onSelect={handleClientSelect}
        />

        <AccessControl pageId="accrual" action="create">
          <Button
            disabled={!selectedClient}
            type="primary"
            icon={<PlusOutlined />}
            onClick={calculate}
          >
            Пересчитать отчетность
          </Button>
        </AccessControl>
      </ActionsWrapper>

      <AccrualsTable
        dataSource={accruals}
        rows={rowItems}
        loading={loading}
        filters={filters}
        onFilter={handleFilters}
      />
    </>
  );
}

export { Accruals };
